import { Injectable, inject } from '@angular/core';
import { Observable, of, ReplaySubject } from 'rxjs';
import { catchError, first, switchMap, tap, share, filter, map } from 'rxjs/operators';
import { CommonDataService } from '../common-data.service';
import { CmsApiService } from '../api/cms-api.service';
import { CookieService, PlatformService } from 'ngx-unificator/services';
import { Router } from '@angular/router';
import { CacheControlService } from '../cache-control.service';
import { LocalstorageService } from '../localstorage.service';
import { PlatformGraphQlService } from '../graphql/platform-graphql.service';
import { GraphQLTypes } from '../graphql/types.graphql';
import { symbolList } from '../../helpers/currency-symbols';
import { GlobalFieldsConfig } from '../../helpers/global-field-config';
import { PROFILE_ADDRESS_REQUERIED_FIELDS, PROFILE_REQUERIED_FIELDS } from './data/user.data';
import { LanguageService } from '../language.service';
import { ScriptLoaderService } from '../script-loader.service';
import { StagDetectorService } from '../stag-detector.service';
import { EnvironmentService } from '../environment.service';
import { StaticContentService } from '../static-content.service';
import { FAST_DEP_REGISTRATION_BANNER, FAST_DEP_REGISTRATION_SEGMENT } from './data/user.local-storage';
import { filterFirst } from '../../shared/pipes/filter-first.pipe';
import { WA_WINDOW } from 'ngx-unificator/tokens';
import { DOCUMENT } from '@angular/common';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private _cmsApi = inject(CmsApiService);
  private _commonData = inject(CommonDataService);
  private _cookies = inject(CookieService);
  private _router = inject(Router);
  private _cache = inject(CacheControlService);
  private _localStorage = inject(LocalstorageService);
  private _platformApi = inject(PlatformGraphQlService);
  private _lang = inject(LanguageService);
  private _scriptLoader = inject(ScriptLoaderService);
  private _stag = inject(StagDetectorService);
  private _env = inject(EnvironmentService);
  private _static = inject(StaticContentService);
  private _platform = inject(PlatformService);
  private _window = inject(WA_WINDOW);
  private _document = inject(DOCUMENT);

  /**
   * Emits when user auth status ready
   */
  private _auth$: ReplaySubject<boolean> = new ReplaySubject<boolean>(1);

  /**
   * Is user authorized
   */
  private _auth: boolean;

  /**
   * User info
   */
  private _info: any = {};

  /**
   * User`s active currency (account)
   */
  private _currentWallet: any;

  /**
   * Active payment session
   */
  private _сurrentPaymentSessionId: string;

  /**
   * Emits when user currency changed
   */
  private _currentCurrency$: ReplaySubject<string> = new ReplaySubject<string>(1);

  /**
   * List of user accounts (currencies)
   */
  private _accountList: Array<any> = [];

  /**
   * Emits list of user accounts (currencies)
   */
  private _accountList$: ReplaySubject<any> = new ReplaySubject<any>(1);

  /**
   * List of user active accounts (wallets)
   */
  private _accountWalletList: any[] = [];

  /**
   * Emits list of user active accounts (wallets)
   */
  private _accountWalletList$: ReplaySubject<any> = new ReplaySubject<any>(1);

  /**
   * Has user already been registered before (by cookie)
   */
  private _isUserAlreadyRegistered: boolean;

  /**
   * Indicates the fast deposit segment for the user.
   */
  private _fastDepositSegment: string = null;

  constructor() {
    this.fetchAllUserData();
    this.checkUserAlreadyRegistered();
  }

  /**
   * Access to private fields from outside
   */
  get auth(): boolean {
    return this._auth;
  }

  get auth$(): ReplaySubject<boolean> {
    return this._auth$;
  }

  get info(): any {
    return this._info;
  }

  get currentWallet(): any {
    return this._currentWallet;
  }

  get totalBalance(): any {
    return +this.currentWallet?.amount + (+this.currentWallet?.bonusBalance || 0);
  }

  get currency$(): ReplaySubject<string> {
    return this._currentCurrency$;
  }

  get accountList(): Array<any> {
    return this._accountList;
  }

  get accountList$(): ReplaySubject<any> {
    return this._accountList$;
  }

  get accountWalletList(): Array<any> {
    return this._accountWalletList;
  }

  get accountWalletList$(): ReplaySubject<any> {
    return this._accountWalletList$;
  }

  get сurrentPaymentSessionId(): string {
    return this._сurrentPaymentSessionId;
  }

  /**
   * Access to _isUserAlreadyRegistered
   */
  get isUserAlreadyRegistered(): boolean {
    return this._isUserAlreadyRegistered;
  }

  /**
   * Check if user allready registered and return dymanic translate key
   */
  public get authKeyTranslate(): string {
    return this.isUserAlreadyRegistered ? 'btn.signin' : 'btn.signup';
  }

  public get isCA(): boolean {
    return this._env.env?.country?.short === 'ca' || (this.auth && this.info?.country === 'CA');
  }

  public get isAU(): boolean {
    return this._env.env?.country?.short === 'au' || (this.auth && this.info?.country === 'AU');
  }

  public get isDE(): boolean {
    return this._env.env?.country?.short === 'de' || (this.auth && this.info?.country === 'DE');
  }

  public get isIE(): boolean {
    return this._env.env?.country?.short === 'ie' || (this.auth && this.info?.country === 'IE');
  }

  public get isES(): boolean {
    return this._env.env?.country?.short === 'es' || (this.auth && this.info?.country === 'ES');
  }

  public get isEE(): boolean {
    return this._env.env?.country?.short === 'ee' || (this.auth && this.info?.country === 'EE');
  }

  public get isPL(): boolean {
    return this._env.env?.country?.short === 'pl' || (this.auth && this.info?.country === 'PL');
  }

  public get isUserWithStateCountries(): boolean {
    return this.isCA || this.isAU;
  }

  public get showStateField(): boolean {
    return this.isUserWithStateCountries && !this.info?.country;
  }

  public get fastDepositSegment(): string {
    return this._fastDepositSegment;
  }

  /**
   * Get user account & wallet list and save to local var
   */
  getUserAccountsAndWallets(networkOnly: boolean = false): Observable<any> {
    return this._commonData.loaded$.pipe(
      switchMap(() => {
        return this._platformApi.userPaymentInfo(networkOnly).pipe(
          first(),
          tap(res => {
            this._accountWalletList = this._resolveWalletList(res?.data?.result?.info?.player?.walletsData?.walletList) || [];
            this._accountWalletList$.next(this._accountWalletList);
            this._accountList = this._resolveAccountList(this._commonData.currencyList) || [];
            this._accountList$.next(this._accountList);
            this._updateCurrentCurrencyBalance();
          }),
        );
      }),
    );
  }

  /**
   * Get user info and save to local var
   */
  getUserInfo(networkOnly: boolean = false): Observable<any> {
    this.waitingForPlayerRequest();
    return this._platformApi.userInfo(networkOnly).pipe(
      first(),
      catchError(() => of({})),
      tap(e => {
        this.applyUserInfo(e?.data?.result?.info || {});
        this.resolveRequiredFields();
      }),
    );
  }

  updateAllAccountsWalletList() {
    return this.getUserAccountsAndWallets(true);
  }

  /**
   * Save user info to local var and declare user state
   * @param info
   */
  applyUserInfo(data: GraphQLTypes.PlayerAuth) {
    const previousAuthValue = this._auth;
    this._resolveIserInfo(data);
    if (data?.loggedIn) {
      this._localStorage.set('user_id', `${data?.player?.id}`);
      this._cmsApi.customHeadersList.push({ key: 'UID', val: `${data?.player?.id}` });
      this._cmsApi.customNoLangHeadersList.push({ key: 'UID', val: `${data?.player?.id}` });
      if (data?.player?.address?.country) {
        this._cmsApi.customHeadersList.push({ key: 'UC', val: data?.player?.address?.country });
        this._cmsApi.customNoLangHeadersList.push({ key: 'UC', val: data?.player?.address?.country });
      }
      if (data?.player?.segments && data?.player?.segments?.length) {
        const statusesHeaders = {
          key: 'U-GROUP',
          val: data?.player?.segments.map(status => status).join(','),
        };
        this._cmsApi.customHeadersList.push(statusesHeaders);
        this._cmsApi.customNoLangHeadersList.push(statusesHeaders);
      }
      this._auth = true;
      this._scriptLoader.load('https://cdn.seondf.com/js/v5/agent.js', 'body').pipe(first()).subscribe();
      this.auth$.next(true);
      this._cache.userAuth$.next(true);
    } else {
      this._auth = false;
      this._cache.userAuth$.next(false);
      this.auth$.next(false);
    }

    if (previousAuthValue !== this._auth) {
      this._cache.updateCache();
    }
  }

  private _resolveIserInfo(data) {
    const addressWithoutId = { ...data?.player?.address };
    delete addressWithoutId.id;

    this._info = {
      ...(data?.player || {}),
      ...(addressWithoutId || null),
      dob: data?.player?.dob?.split('-')?.reverse()?.join('-') || null,
      segments: [...data?.player?.segments || []],
    };
  }

  /**
   * Resolves the required fields for the user's profile based on certain conditions.
   * If the user is in the "fast registration" segment, the required fields are set to an empty array.
   * Otherwise, the function checks the user's information and the global fields configuration to determine
   * which fields are required and adds them to the `requiredFields` array.
   * The resolved required fields are then set on the `_info` object.
   */
  public resolveRequiredFields() {
    const requiredFields = [];
    let depositRequiredFields = [];

    PROFILE_REQUERIED_FIELDS.forEach(e => {
      if (!this._info[e] && GlobalFieldsConfig[e]) {
        requiredFields.push(GlobalFieldsConfig[e]);
        depositRequiredFields.push(GlobalFieldsConfig[e]);
      }
    });

    PROFILE_ADDRESS_REQUERIED_FIELDS(this.isUserWithStateCountries).forEach(e => {
      if (!this._info?.[e] && GlobalFieldsConfig[e]) {
        requiredFields.push(GlobalFieldsConfig[e]);
        depositRequiredFields.push(GlobalFieldsConfig[e]);
      }
    });

    if (Boolean(this.info?.segments?.find(segment => segment === (this._localStorage?.get(FAST_DEP_REGISTRATION_SEGMENT) || '')))) {
      depositRequiredFields = [];
    }

    this._info.requiredFields = requiredFields;
    this._info.depositRequiredFields = depositRequiredFields;
  }

  private _resolveWalletList(list: any[]) {
    return list?.map(e => {
      return { ...e?.node, symbol: (symbolList.find(s => s?.currency === e?.node?.currency) || {})?.symbol || e?.node?.currency };
    });
  }

  private _resolveAccountList(list: any[]) {
    return list?.map(e => {
      return { ...e, currency: e.code };
    });
  }

  /**
   * Fetch all required user data
   *
   * @private
   */
  public fetchAllUserData() {
    const observable = this.getUserInfo(true).pipe(
      first(),
      switchMap(r => {
        return this.getUserAccountsAndWallets(true);
      }),
      share(),
    );
    observable.subscribe();
    return observable;
  }

  /**
   * Update current currency balance info
   *
   * @private
   */
  private _updateCurrentCurrencyBalance() {
    this._currentWallet = this._accountWalletList.find(account => account?.currency === this._info?.activeWallet?.currency) || {};
    this._currentCurrency$.next(this._currentWallet.currency);
  }

  public setCurrentSessionId(id: string): void {
    this._сurrentPaymentSessionId = id;
  }

  /**
   * Change user currency account
   * @param selectedCurrency
   */
  public changeCurrencyAcc(selectedCurrency: string) {
    if (selectedCurrency) {
      this._currentCurrency$.next(null);
      const walletId = (this._accountWalletList.find(e => e?.currency === selectedCurrency) || {})?.id;
      this._platformApi.setWallet({ walletId }).subscribe(() => {
        this.fetchAllUserData();
      });
    }
  }

  /**
   * Check in cookie has user already been registered
   */
  public checkUserAlreadyRegistered() {
    this._isUserAlreadyRegistered = this._cookies.check('registered');
  }

  /**
   * Check if user allready registered and redirect
   */
  public authUser() {
    return this._router.navigateByUrl(this._cookies.check('registered') ? '/login' : '/register');
  }

  /**
   * Waiting for /player response 2 seconds and emit default value
   * @private
   */
  private waitingForPlayerRequest() {
    setTimeout(() => {
      if (this._auth === undefined) {
        this.applyUserInfo({ loggedIn: false });
      }
    }, 2000);
  }

  public updateUserLang() {
    this._lang.langChange$.subscribe(lang => {
      if (this.auth) {
        this._platformApi.updateUsersLang(lang).pipe(first()).subscribe();
      }
    });
  }

  /**
   * Resolves the fast deposit information for the user.
   * If the fast deposit registration segment is stored in local storage, it sets the `_isFastDeposit` and `_fastDepositSegment` properties.
   * Otherwise, it fetches the fast deposit information from the `_static` service and sets the `_isFastDeposit` and `_fastDepositSegment` properties based on the response.
   */
  public resolveFastDeposit() {
    if (this._localStorage.get(FAST_DEP_REGISTRATION_SEGMENT)) {
      this._fastDepositSegment = this._localStorage.get(FAST_DEP_REGISTRATION_SEGMENT);
    } else {
      this._static.item({ slug: 'fast-deposit' }).pipe(
        filter(data => !!data),
        first(),
        map(data => data[0]),
        tap(data => {
          if (data?.Content) {
            this._fastDepositSegment = data?.Content;
            this._localStorage.set(FAST_DEP_REGISTRATION_SEGMENT, this._fastDepositSegment);
            this._localStorage.set(FAST_DEP_REGISTRATION_BANNER, FAST_DEP_REGISTRATION_BANNER);
          }
        })
      ).subscribe();
    }
  }

  /**
   * Checks and redirects users based on registration status and static content.
   * If running in a browser, fetches restriction settings and redirects registered users
   * to a specified URL or to a 403 forbidden page if not registered.
   */
  public checkUsersForRedirect() {
    if (this._platform.isBrowser && environment.production) {
      this._static.item({ slug: 'country-restrict' }).pipe(
        filterFirst(e => !!e),
        map(data => data[0]),
      ).subscribe((e) => {
        if (e) {
          if (this.isDE || this.isIE || this.isES || this.isEE || this.isPL) {
            const redirect: boolean = !!(this._localStorage.get('user_id') || this._localStorage.get('id_id'));
            if (redirect) {
              if (this._localStorage.get('id_id')) {
                this._window.location.href = e.Content + this._window?.location?.pathname + '?affiliate_id=' + this._localStorage.get('id_id');
              } else {
                this._window.location.href = e.Content + this._window?.location?.pathname;
              }
              return;
            }
          }
          const mirror = this._document.createElement('iframe');
          mirror.style.display = 'none';
          mirror.src = e.Content + '/post-message';
          this._document.body.appendChild(mirror);
          const receiveMessage = (event) => {
            console.log('Message received from', event.origin);
            console.log('Data:', event.data);
            if (event.origin !== e.Content) {
              console.warn('Received message from unexpected origin:', event.origin);
              return;
            }
            if (typeof event.data === 'boolean' && event.data) {
              this._window.location.href = e.Content + this._window?.location?.pathname;
            } else {
              this._window.location.href = '/403';
            }
          }
          this._window.addEventListener('message', receiveMessage, false);
        }
      });
    }
  }

}
